import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Activity, Gift, FileText, Download, CheckCircle2, Circle, Search, Pencil, Trash2, ShieldAlert, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { QRCodeSVG } from "qrcode.react";
import MemberPDFButton from "../../components/MemberPDFButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PREDEFINED_GOODIES = ["Gym Bag", "T-Shirt", "Shaker Bottle"];

const EditMemberModal = ({ onClose, onEdit, member, packages }) => {
  const [formData, setFormData] = useState(member);

  const handleSubmit = (e) => {
    e.preventDefault();
    onEdit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Edit Profile</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Full Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Email</label>
              <input type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Phone</label>
              <input type="text" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Status</label>
              <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Active</option>
                <option>Inactive</option>
                <option>Paused</option>
                <option>Frozen</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Package</label>
              <select value={formData.package} onChange={e=>setFormData({...formData, package: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                {packages.map(pkg => <option key={pkg.id} value={pkg.name}>{pkg.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Address</label>
            <input type="text" value={formData.address} onChange={e=>setFormData({...formData, address: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

const MemberRow = ({ m, expandedId, setExpandedId, activeTab, setActiveTab, inputValue, setInputValue, handleUpdate, toggleGoody, onEditOpen, onDelete, trainers, handlePTLog, handleMeasurement, handlePhoto }) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const isExpired = m.expiry_date && m.expiry_date < todayStr;
  const [ptNotes, setPtNotes] = useState("");
  const [ptTrainer, setPtTrainer] = useState(trainers.length > 0 ? trainers[0].name : "");
  const emptyMeasure = { weight: "", body_fat: "", chest: "", waist: "", hips: "", arms: "", thighs: "", bmi: "" };
  const [measure, setMeasure] = useState(emptyMeasure);
  const [photoLabel, setPhotoLabel] = useState("Before");

  const submitMeasure = () => {
    const payload = {};
    Object.keys(measure).forEach(k => { payload[k] = parseFloat(measure[k]) || 0; });
    handleMeasurement(m.id, payload);
    setMeasure(emptyMeasure);
  };

  const onPhotoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => handlePhoto(m.id, { label: photoLabel, image: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className={`bg-card_bg border rounded-2xl overflow-hidden shadow-sm relative group transition-all duration-300 hover:-translate-y-1 ${isExpired ? 'border-accent_danger/40 hover:border-accent_danger' : 'border-border_soft hover:border-primary/40'}`}>
      <div className="absolute top-5 right-20 flex gap-2 opacity-0 md:opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-card_bg/80 backdrop-blur-sm p-1 rounded-lg border border-border_soft/50">
        <button onClick={(e) => { e.stopPropagation(); onEditOpen(m); }} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Edit Core Details"><Pencil size={16}/></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(m.id); }} className="p-1.5 text-text_secondary hover:text-accent_danger transition-colors" title="Delete Profile"><Trash2 size={16}/></button>
      </div>
      
      <div 
        className="p-6 md:p-8 flex flex-col md:flex-row justify-between items-start md:items-center cursor-pointer hover:bg-card_bg/40 transition-colors gap-4"
        onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
      >
        <div>
          <h3 className="text-xl font-bold text-text_primary flex items-center gap-3 mb-2 tracking-tight">
            <span className="font-mono text-gold text-sm mr-2 opacity-80">{m.id}</span>
            {m.name} 
            {!isExpired && <span className={`px-2.5 py-1 text-[10px] uppercase font-bold tracking-widest rounded-md ${m.status==='Active' ? 'bg-accent_success/10 text-accent_success border border-accent_success/20' : 'bg-text_secondary/10 text-text_secondary border border-text_secondary/20'}`}>{m.status}</span>}
            {isExpired && <span className="px-2.5 py-1 text-[10px] uppercase rounded-md bg-accent_danger/10 text-accent_danger border border-accent_danger/20 font-bold flex items-center gap-1.5"><ShieldAlert size={12}/> Expired</span>}
          </h3>
          <p className="text-sm text-text_secondary font-medium">{m.package} <span className="mx-3 opacity-30">|</span> Expiry: <span className={isExpired ? 'text-accent_danger font-bold ml-1' : 'ml-1 text-text_primary opacity-90'}>{m.expiry_date || 'N/A'}</span></p>
          {m.phone && <p className="text-xs text-text_secondary/70 mt-2">{m.phone} <span className="mx-2">•</span> {m.email}</p>}
        </div>
        <div className="flex gap-5 text-gold items-center bg-card_bg/50 px-4 py-3 rounded-xl border border-border_soft/30">
          {m.pt_sessions_remaining > 0 && (
            <div className="text-center mr-2 border-r border-border_soft/30 pr-5">
              <p className="text-[10px] text-text_secondary uppercase tracking-widest font-bold mb-1">PT Left</p>
              <p className="text-2xl font-bold leading-none">{m.pt_sessions_remaining}</p>
            </div>
          )}
          <MemberPDFButton member={m} />
          <Activity size={20} className="opacity-80" />
          <Gift size={20} className="opacity-80" />
          <FileText size={20} className="opacity-80" />
        </div>
      </div>

      {expandedId === m.id && (
        <div className="border-t border-border_soft/40 p-6 md:p-8 bg-card_bg/20 animate-in slide-in-from-top-4 duration-300">
          <div className="flex gap-3 border-b border-border_soft/30 mb-6 pb-3 overflow-x-auto">
            {['bmi', 'measure', 'photos', 'pt', 'goodies', 'docs', 'qr'].map(tab => (
              <button 
                key={tab}
                onClick={() => setActiveTab(tab)} 
                className={`text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-lg transition-all whitespace-nowrap ${activeTab===tab ? 'bg-primary/10 text-gold border border-primary/20' : 'text-text_secondary hover:text-text_primary hover:bg-black/5'}`}
              >
                {tab === 'bmi' ? 'Body Stats' : tab === 'measure' ? 'Measurements' : tab === 'photos' ? 'Progress Photos' : tab === 'pt' ? 'PT Sessions' : tab === 'goodies' ? 'Goodies' : tab === 'docs' ? 'Documents' : 'QR Code'}
              </button>
            ))}
          </div>

          {activeTab === 'bmi' && (
            <div className="space-y-6">
              <div className="flex gap-3 max-w-md">
                <input type="number" placeholder="Enter new BMI..." value={inputValue} onChange={e=>setInputValue(e.target.value)} className="bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary text-sm focus:border-primary outline-none flex-1" />
                <button onClick={() => handleUpdate('bmi', m.id)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl text-sm font-bold shrink-0 shadow-md">Save</button>
              </div>

              {m.bmi_history && m.bmi_history.length > 0 ? (
                <div className="h-56 w-full bg-card_bg/50 rounded-2xl p-6 border border-border_soft/20 shadow-inner">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={m.bmi_history}>
                      <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                      <YAxis domain={['auto', 'auto']} stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                      <RechartsTooltip contentStyle={{backgroundColor: '#091A0F', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '8px'}} itemStyle={{color: '#c5a059', fontWeight: 'bold'}} />
                      <Line type="monotone" dataKey="value" stroke="#c5a059" strokeWidth={3} dot={{r: 5, fill: '#c5a059', stroke: '#020805', strokeWidth: 2}} activeDot={{r: 8}} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-text_secondary italic bg-card_bg/50 p-4 rounded-xl inline-block border border-border_soft/20">No body stats recorded yet.</p>
              )}
            </div>
          )}

          {activeTab === 'measure' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["weight","Weight (kg)"],["bmi","BMI"],["body_fat","Body Fat %"],["chest","Chest"],["waist","Waist"],["hips","Hips"],["arms","Arms"],["thighs","Thighs"]].map(([k,label])=>(
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-wider text-text_secondary font-bold mb-1 block">{label}</label>
                    <input type="number" value={measure[k]} onChange={e=>setMeasure({...measure,[k]:e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-lg p-2.5 text-text_primary text-sm outline-none focus:border-primary" />
                  </div>
                ))}
              </div>
              <button data-testid="save-measurement-btn" onClick={submitMeasure} className="bg-primary text-[#111827] px-6 py-3 rounded-xl text-sm font-bold">Save Measurement</button>
              {m.measurements_history && m.measurements_history.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-border_soft/40">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-main_bg text-text_secondary text-[10px] uppercase tracking-wider"><tr>
                      <th className="px-3 py-2">Date</th><th className="px-3 py-2">Wt</th><th className="px-3 py-2">BMI</th><th className="px-3 py-2">Fat%</th><th className="px-3 py-2">Chest</th><th className="px-3 py-2">Waist</th><th className="px-3 py-2">Hips</th><th className="px-3 py-2">Arms</th><th className="px-3 py-2">Thighs</th>
                    </tr></thead>
                    <tbody className="divide-y divide-border_soft/30">
                      {m.measurements_history.map((x,i)=>(
                        <tr key={i} className="text-text_primary"><td className="px-3 py-2 text-text_secondary">{x.date}</td><td className="px-3 py-2">{x.weight}</td><td className="px-3 py-2">{x.bmi}</td><td className="px-3 py-2">{x.body_fat}</td><td className="px-3 py-2">{x.chest}</td><td className="px-3 py-2">{x.waist}</td><td className="px-3 py-2">{x.hips}</td><td className="px-3 py-2">{x.arms}</td><td className="px-3 py-2">{x.thighs}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="space-y-5">
              <div className="flex gap-3 items-center">
                <select value={photoLabel} onChange={e=>setPhotoLabel(e.target.value)} className="bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm">
                  <option>Before</option><option>After</option><option>Progress</option>
                </select>
                <label className="bg-primary text-[#111827] px-5 py-3 rounded-xl text-sm font-bold cursor-pointer">
                  Upload Photo
                  <input data-testid="upload-photo-input" type="file" accept="image/*" onChange={onPhotoFile} className="hidden" />
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(m.before_after||[]).map((p)=>(
                  <div key={p.id} className="bg-card_bg border border-border_soft rounded-xl overflow-hidden">
                    <img src={p.image} alt={p.label} className="w-full h-40 object-cover" />
                    <div className="p-2 flex justify-between text-xs"><span className="text-text_primary font-semibold">{p.label}</span><span className="text-text_secondary">{p.date}</span></div>
                  </div>
                ))}
                {(!m.before_after || m.before_after.length===0) && <p className="text-sm text-text_secondary italic">No progress photos yet.</p>}
              </div>
            </div>
          )}

          {activeTab === 'qr' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="bg-white p-4 rounded-xl"><QRCodeSVG value={m.id} size={160} /></div>
              <p className="text-sm text-text_secondary">Scan at the front desk to check in.</p>
              <p className="text-xs text-text_secondary">Member ID: <span className="font-mono text-gold">{m.id}</span></p>
              {m.referral_code && <p className="text-xs text-text_secondary">Referral Code: <span className="font-mono text-gold">{m.referral_code}</span></p>}
            </div>
          )}

          {activeTab === 'pt' && (
            <div className="space-y-6">
              <div className="bg-card_bg/80 p-6 rounded-2xl border border-border_soft/30 shadow-inner">
                <h4 className="text-sm font-bold text-gold mb-4 uppercase tracking-widest">Log PT Session</h4>
                <div className="flex gap-4 flex-col md:flex-row">
                  <select value={ptTrainer} onChange={e=>setPtTrainer(e.target.value)} className="bg-main_bg border border-border_soft rounded-xl p-3.5 text-text_primary text-sm outline-none shrink-0 md:w-64 focus:border-primary">
                    <option value="">-- Select Trainer --</option>
                    {trainers.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                  <input type="text" placeholder="Session notes..." value={ptNotes} onChange={e=>setPtNotes(e.target.value)} className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3.5 text-text_primary text-sm outline-none focus:border-primary" />
                  <button onClick={() => { handlePTLog(m.id, { trainer: ptTrainer, notes: ptNotes, image_url: "" }); setPtNotes(""); }} className="bg-primary text-[#111827] px-8 py-3.5 rounded-xl text-sm font-bold shrink-0 shadow-md">Save Session</button>
                </div>
              </div>
              <div className="space-y-3">
                {m.pt_logs && m.pt_logs.length > 0 ? m.pt_logs.map((log, i) => (
                  <div key={i} className="flex justify-between items-center bg-card_bg p-5 rounded-2xl border border-border_soft/20 text-sm shadow-sm">
                    <div>
                      <p className="text-text_primary font-bold text-base mb-1">{log.trainer}</p>
                      <p className="text-text_secondary leading-relaxed">{log.notes}</p>
                    </div>
                    <span className="text-xs font-mono text-gold bg-primary/10 px-3 py-1.5 rounded-lg border border-primary/20 shrink-0 ml-4">{log.date}</span>
                  </div>
                )) : <p className="text-sm text-text_secondary italic px-2">No PT sessions logged yet.</p>}
              </div>
            </div>
          )}

          {activeTab === 'goodies' && (
            <div className="space-y-5">
              <p className="text-sm text-text_secondary">Track goodies given to this member.</p>
              <div className="flex gap-4 flex-wrap">
                {PREDEFINED_GOODIES.map(item => {
                  const hasItem = m.goodies?.includes(item);
                  return (
                    <button 
                      key={item}
                      onClick={() => toggleGoody(m.id, item, m.goodies || [])}
                      className={`flex items-center gap-3 px-6 py-3.5 rounded-xl border transition-all duration-300 font-medium ${hasItem ? 'bg-accent_success/10 border-accent_success/50 text-accent_success shadow-[0_0_15px_rgba(52,211,153,0.15)]' : 'bg-card_bg border-border_soft/50 text-text_secondary hover:text-text_primary hover:border-primary/40'}`}
                    >
                      {hasItem ? <CheckCircle2 size={18} strokeWidth={2.5} /> : <Circle size={18} strokeWidth={1.5} />}
                      {item}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {activeTab === 'docs' && (
            <div className="space-y-6">
              <div className="flex gap-3 max-w-lg">
                <input type="text" placeholder="File URL or name..." value={inputValue} onChange={e=>setInputValue(e.target.value)} className="bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary text-sm focus:border-primary outline-none flex-1" />
                <button onClick={() => handleUpdate('docs', m.id)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl text-sm font-bold shadow-md shrink-0">Add File</button>
              </div>
              <div className="flex gap-3 flex-wrap">
                {m.documents?.map((d,i) => (
                  <div key={`doc-${i}`} className="flex items-center gap-3 bg-card_bg p-3 rounded-xl border border-border_soft/30 hover:border-primary/40 transition-colors group">
                    <div className="bg-primary/10 p-2 rounded-lg text-gold"><FileText size={16}/></div>
                    <span className="text-sm font-medium text-text_primary pr-2">{d}</span>
                    <Download size={16} className="text-text_secondary cursor-pointer hover:text-text_primary opacity-50 group-hover:opacity-100 transition-opacity"/>
                  </div>
                ))}
                {(!m.documents || m.documents.length === 0) && <p className="text-sm text-text_secondary italic mt-2">No documents yet.</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Members() {
  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("bmi");
  const [expandedId, setExpandedId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [showEdit, setShowEdit] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/members`);
      setMembers(res.data);
      const pkgRes = await axios.get(`${BACKEND_URL}/api/packages`);
      setPackages(pkgRes.data);
      const hrRes = await axios.get(`${BACKEND_URL}/api/hr`);
      setTrainers(hrRes.data.filter(t => t.role.toLowerCase().includes('trainer') || t.role.toLowerCase().includes('coach')));
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleUpdate = async (type, memberId) => {
    if(!inputValue) return;
    try {
      await axios.post(`${BACKEND_URL}/api/members/${memberId}/${type}`, { value: inputValue });
      fetchMembers();
      setInputValue("");
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/members/${data.id}`, data);
      setShowEdit(null);
      fetchMembers();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this member permanently?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/members/${id}`);
        fetchMembers();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  const toggleGoody = async (memberId, item, currentGoodies) => {
    const newGoodies = currentGoodies.includes(item) 
      ? currentGoodies.filter(g => g !== item)
      : [...currentGoodies, item];
    try {
      await axios.post(`${BACKEND_URL}/api/members/${memberId}/goodies_sync`, { goodies: newGoodies });
      fetchMembers();
    } catch(e) { console.error(e); }
  };

  const handlePTLog = async (memberId, sessionData) => {
    if(!sessionData.trainer || !sessionData.notes) return;
    try {
      await axios.post(`${BACKEND_URL}/api/members/${memberId}/pt_session`, sessionData);
      fetchMembers();
    } catch(e) { console.error(e); }
  };

  const handleMeasurement = async (memberId, data) => {
    try { await axios.post(`${BACKEND_URL}/api/members/${memberId}/measurement`, data); fetchMembers(); } catch(e){ console.error(e); }
  };

  const handlePhoto = async (memberId, data) => {
    try { await axios.post(`${BACKEND_URL}/api/members/${memberId}/photo`, data); fetchMembers(); } catch(e){ console.error(e); }
  };

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    m.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.phone?.includes(searchTerm) || 
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Members</h1>
          <p className="text-text_secondary text-sm">Manage member profiles, body stats, PT sessions and documents.</p>
        </div>
        <Link to="/admin/members/new" className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md w-fit">
          <Plus size={20}/> Add Member
        </Link>
      </div>

      <div className="bg-card_bg border border-border_soft/50 rounded-2xl overflow-hidden shadow-sm p-2.5 flex items-center gap-4 mb-8">
        <Search size={22} className="text-text_secondary ml-4 opacity-70" />
        <input 
          type="text" 
          placeholder="Search by name, member ID, email or phone..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-transparent border-none text-text_primary font-medium focus:outline-none p-3 text-sm placeholder:text-text_secondary/50"
        />
      </div>

      <div className="space-y-5">
        {filteredMembers.length === 0 && <p className="text-text_secondary col-span-full">No members yet.</p>}
        {filteredMembers.map(m => (
          <MemberRow 
            key={m.id}
            m={m} 
            expandedId={expandedId} 
            setExpandedId={setExpandedId} 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleUpdate={handleUpdate}
            toggleGoody={toggleGoody}
            onEditOpen={setShowEdit}
            onDelete={handleDelete}
            trainers={trainers}
            handlePTLog={handlePTLog}
            handleMeasurement={handleMeasurement}
            handlePhoto={handlePhoto}
          />
        ))}
      </div>

      {showEdit && <EditMemberModal member={showEdit} packages={packages} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}
    </div>
  );
}
